import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { SEOPages } from '../components/SEO';
import { 
  Clock,
  TrendingDown, 
  AlertCircle, 
  CheckCircle, 
  ArrowRight,
  Calculator,
  DollarSign,
  Award,
  Shield,
  Target
} from 'lucide-react';
import LeadMagnetBox from '../components/LeadMagnetBox';
import MiniSimulateurRetraiteAnticipee from '../components/MiniSimulateurRetraiteAnticipee';

const GuidePlanificationRetraiteAnticipee = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0F12] via-[#0C1D3D] to-[#0B0F12]">
      <SEO {...SEOPages.guideRetraiteAnticipee} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10" />
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-pink-400 to-red-400 bg-clip-text text-transparent leading-tight">
              Guide Retraite Anticipée Suisse
            </h1>
            <p className="text-xl text-gray-300 mb-4">
              Stratégies, optimisation fiscale et calculs pour partir en retraite avant 65 ans
            </p>
            <p className="text-sm text-gray-400">
              Par ATOM Group SA • Mis à jour le {new Date().toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' })} • Lecture : 18 minutes
            </p>
          </div>

          {/* Lead Magnet Top */}
          <LeadMagnetBox position="top" />
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Applicabilité nationale */}
          <div className="p-5 bg-purple-500/10 border-l-4 border-purple-500 rounded-r-lg mb-8">
            <p className="text-gray-200 text-sm">
              <strong>🇨🇭 Guide applicable dans toute la Suisse</strong> • Les règles AVS et LPP sont fédérales (âges, réductions, conditions). Seule la fiscalité du retrait en capital varie selon les cantons. Ce guide s'applique à toute la Suisse romande. <strong>Mis à jour : janvier 2025</strong>
            </p>
          </div>
          
          {/* Introduction */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Est-il possible de partir en retraite avant 65 ans ?</h2>
            <p className="text-gray-300 mb-4 leading-relaxed">
              La retraite anticipée est un rêve pour beaucoup, mais une réalité pour ceux qui planifient avec rigueur. 
              En Suisse, partir en retraite avant l'âge légal (65 ans pour les hommes. Pour les femmes, l'âge de référence 
              est en transition progressive selon AVS21 vers 65 ans d'ici 2028) nécessite une stratégie financière solide 
              et une compréhension précise des impacts.
            </p>
            <p className="text-gray-300 mb-4 leading-relaxed">
              Ce guide vous accompagne pas à pas pour évaluer la faisabilité de votre projet, optimiser vos revenus 
              de retraite et éviter les pièges fiscaux coûteux.
            </p>
            
            {/* ATOM Independence */}
            <div className="mt-6 p-4 bg-purple-500/10 border-l-4 border-purple-500 rounded-r-lg">
              <p className="text-gray-200 text-sm italic">
                <strong>ATOM Group est un cabinet 100% indépendant,</strong> non lié à une banque ou assurance. 
                Nos recommandations sont objectives et basées uniquement sur votre intérêt.
              </p>
            </div>
          </div>

          {/* Lead Magnet après intro */}

          {/* CTA après introduction */}
          

          {/* Impact AVS */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <TrendingDown className="w-8 h-8 text-red-400" />
              <h2 className="text-3xl font-bold text-white">Impact sur la rente AVS</h2>
            </div>
            
            <p className="text-gray-300 mb-6 leading-relaxed">
              La rente AVS peut être anticipée jusqu'à 2 ans maximum (dès 63 ans). Mais attention : chaque année 
              d'anticipation entraîne une réduction définitive de <strong className="text-red-400">6.8% par an</strong>.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-left py-3 px-2 md:py-4 md:px-4 text-gray-300 font-semibold text-sm md:text-base">Âge départ</th>
                    <th className="text-left py-3 px-2 md:py-4 md:px-4 text-gray-300 font-semibold text-sm md:text-base">Anticipation</th>
                    <th className="text-left py-3 px-2 md:py-4 md:px-4 text-gray-300 font-semibold text-sm md:text-base">Réduction</th>
                    <th className="text-left py-3 px-2 md:py-4 md:px-4 text-gray-300 font-semibold text-sm md:text-base">Rente/mois</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300 text-sm md:text-base">
                  <tr className="border-b border-white/10">
                    <td className="py-3 px-2 md:py-4 md:px-4 font-medium">65 ans</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">Aucune</td>
                    <td className="py-3 px-2 md:py-4 md:px-4 text-green-400 font-bold">0%</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">CHF 2'520</td>
                  </tr>
                  <tr className="border-b border-white/10">
                    <td className="py-3 px-2 md:py-4 md:px-4 font-medium">64 ans</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">1 an</td>
                    <td className="py-3 px-2 md:py-4 md:px-4 text-yellow-400 font-bold">-6.8%</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">CHF 2'350</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-2 md:py-4 md:px-4 font-medium">63 ans</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">2 ans max</td>
                    <td className="py-3 px-2 md:py-4 md:px-4 text-orange-400 font-bold">-13.6%</td>
                    <td className="py-3 px-2 md:py-4 md:px-4">CHF 2'177</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-xs text-gray-400 mt-4 text-center italic">
              * Rente maximale AVS 2026. La réduction est définitive et s'applique à vie.
            </p>

            <div className="mt-6 p-4 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
              <p className="text-gray-200 text-sm">
                <strong>Important :</strong> Cette réduction est permanente. L'anticipation AVS est limitée à 2 ans maximum. 
                Chaque année d'anticipation représente une perte définitive de 6.8% de votre rente, soit des dizaines de milliers 
                de francs sur toute votre retraite.
              </p>
            </div>
          </div>

          {/* CTA après impact AVS */}
          

          {/* Stratégies 2ème et 3ème pilier */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-8 h-8 text-blue-400" />
              <h2 className="text-3xl font-bold text-white">Optimiser le 2ème et 3ème pilier</h2>
            </div>

            <h3 className="text-xl font-bold text-white mb-4">2ème pilier (LPP)</h3>
            <p className="text-gray-300 mb-4 leading-relaxed">
              Le 2ème pilier peut généralement être retiré dès 58 ans en cas de départ en retraite (certaines caisses fixent un âge différent, comme 60 ans). Deux options possibles :
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="p-6 bg-blue-500/10 rounded-xl border border-blue-500/30">
                <h4 className="font-bold text-white mb-3">💰 Retrait en capital</h4>
                <p className="text-gray-300 mb-3 text-sm"><strong>Avantages :</strong></p>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Disponibilité immédiate des fonds</li>
                  <li>• Imposition séparée avantageuse</li>
                  <li>• Flexibilité d'utilisation</li>
                </ul>
                <p className="text-gray-300 mt-3 mb-2 text-sm"><strong>Inconvénients :</strong></p>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Risque de mauvaise gestion</li>
                  <li>• Plus d'assurance décès/invalidité</li>
                </ul>
              </div>

              <div className="p-6 bg-purple-500/10 rounded-xl border border-purple-500/30">
                <h4 className="font-bold text-white mb-3">📈 Rente viagère</h4>
                <p className="text-gray-300 mb-3 text-sm"><strong>Avantages :</strong></p>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Revenu garanti à vie</li>
                  <li>• Pas de risque de placement</li>
                  <li>• Rente de survivant</li>
                </ul>
                <p className="text-gray-300 mt-3 mb-2 text-sm"><strong>Inconvénients :</strong></p>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Taux de conversion faible</li>
                  <li>• Pas d'héritage du capital</li>
                </ul>
              </div>
            </div>

            <h3 className="text-xl font-bold text-white mb-4 mt-8">3ème pilier (3a)</h3>
            <p className="text-gray-300 mb-4 leading-relaxed">
              Le 3ème pilier peut être retiré 5 ans avant l'âge AVS. Stratégie optimale :
            </p>

            <div className="space-y-4">
              <div className="p-6 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">✅ Retraits échelonnés</h4>
                <p className="text-gray-300 text-sm">
                  Si vous avez plusieurs comptes 3a, retirez-les sur plusieurs années fiscales pour réduire 
                  l'impôt (progression à froid). Exemple : 3 comptes = 3 années de retraits échelonnés.
                </p>
              </div>

              <div className="p-6 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">✅ Maximiser jusqu'au départ</h4>
                <p className="text-gray-300 text-sm">
                  Continuez à verser le maximum dans votre 3a jusqu'à votre départ effectif en retraite. 
                  Chaque versement = déduction fiscale immédiate.
                </p>
              </div>

              <div className="p-6 bg-green-500/10 border-l-4 border-green-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">✅ Coordonner avec le 2ème pilier</h4>
                <p className="text-gray-300 text-sm">
                  Ne retirez pas tout la même année. Étalez les retraits 3a et 2ème pilier pour minimiser 
                  l'impôt sur les prestations en capital.
                </p>
              </div>
            </div>
          </div>

          {/* CTA WhatsApp */}
          <div className="glass p-6 rounded-xl border border-green-500/30 mb-8 bg-gradient-to-r from-green-500/10 to-emerald-500/10">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="text-3xl">📲</div>
                <div>
                  <p className="text-white font-semibold text-lg">Une question rapide ?</p>
                  <p className="text-gray-300 text-sm">Réponse en 5 minutes</p>
                </div>
              </div>
              <a
                href="https://wa.me/41793754293"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all transform hover:scale-105 flex items-center gap-2 whitespace-nowrap"
              >
                Écrire sur WhatsApp
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Simulateur */}
          <MiniSimulateurRetraiteAnticipee />

          {/* Budget et dépenses */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <DollarSign className="w-8 h-8 text-green-400" />
              <h2 className="text-3xl font-bold text-white">Calculer votre budget retraite</h2>
            </div>

            <p className="text-gray-300 mb-6 leading-relaxed">
              La règle des 70-80% : En retraite, vos dépenses représentent généralement 70 à 80% de votre revenu actuel. 
              Voici pourquoi :
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="p-6 bg-red-500/10 rounded-xl border border-red-500/30">
                <h4 className="font-bold text-white mb-3">❌ Dépenses qui disparaissent</h4>
                <ul className="text-gray-300 text-sm space-y-2">
                  <li>• Cotisations AVS/LPP/AC (environ 15% du salaire)</li>
                  <li>• Frais de transport domicile-travail</li>
                  <li>• Repas à l'extérieur</li>
                  <li>• Versements 3ème pilier</li>
                  <li>• Hypothèque (souvent remboursée)</li>
                </ul>
              </div>

              <div className="p-6 bg-yellow-500/10 rounded-xl border border-yellow-500/30">
                <h4 className="font-bold text-white mb-3">⚠️ Nouvelles dépenses</h4>
                <ul className="text-gray-300 text-sm space-y-2">
                  <li>• Assurance maladie privée (après 65 ans)</li>
                  <li>• Loisirs et voyages augmentés</li>
                  <li>• Santé et soins (avec l'âge)</li>
                  <li>• Aide à domicile éventuelle</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA Simulation */}
          <div className="glass p-8 rounded-2xl border border-purple-500/30 mb-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10">
            <div className="text-center">
              <div className="inline-block bg-gradient-to-br from-purple-500 to-pink-600 p-4 rounded-xl mb-4">
                <Calculator className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                💡 Vous souhaitez connaître votre faisabilité exacte ?
              </h3>
              <p className="text-gray-300 mb-6">
                Obtenez une simulation complète en 60 secondes
              </p>
              <a
                href="#simulateur"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#simulateur')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-700 transition-all transform hover:scale-105 text-lg"
              >
                Lancer la simulation ATOM (gratuit – 60 secondes)
                <ArrowRight className="w-6 h-6" />
              </a>
            </div>
          </div>

          {/* Cas pratiques */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-8 h-8 text-orange-400" />
              <h2 className="text-3xl font-bold text-white">Cas pratiques</h2>
            </div>

            <div className="space-y-6">
              <div className="p-6 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/30">
                <h4 className="text-xl font-bold text-white mb-3">🎯 Cas 1 : Pierre, cadre 58 ans</h4>
                <p className="text-gray-300 mb-2"><strong>Situation :</strong> Salaire CHF 180'000, capital LPP CHF 800'000, 3a CHF 200'000</p>
                <p className="text-gray-300 mb-4"><strong>Objectif :</strong> Retraite à 62 ans</p>
                <p className="text-green-400 font-semibold mb-2">✅ Solution ATOM :</p>
                <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                  <li>Rente AVS anticipée à 64 ans (1 an avant, max autorisé) : CHF 2'350/mois</li>
                  <li>Retrait LPP en capital à 62 ans : CHF 800'000</li>
                  <li>Vivre sur le capital LPP de 62 à 64 ans : CHF 240'000</li>
                  <li>Retrait 3a échelonné sur 3 ans (60, 61, 62 ans)</li>
                  <li>Budget mensuel retraite : CHF 10'000 (vs CHF 12'500 actuel)</li>
                  <li><strong>Résultat :</strong> Retraite viable avec capital restant CHF 560'000</li>
                </ul>
              </div>

              <div className="p-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/30">
                <h4 className="text-xl font-bold text-white mb-3">🎯 Cas 2 : Sophie, indépendante 55 ans</h4>
                <p className="text-gray-300 mb-2"><strong>Situation :</strong> Revenu CHF 120'000, pas de 2ème pilier, 3a CHF 300'000 + placements CHF 400'000</p>
                <p className="text-gray-300 mb-4"><strong>Objectif :</strong> Retraite à 60 ans</p>
                <p className="text-green-400 font-semibold mb-2">✅ Solution ATOM :</p>
                <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                  <li>Vivre sur placements de 60 à 63 ans : CHF 180'000 (CHF 5'000/mois)</li>
                  <li>Rente AVS anticipée à 63 ans (2 ans, max autorisé) : CHF 2'177/mois</li>
                  <li>Retrait 3a à 60 ans : CHF 300'000 (investis à 4%/an)</li>
                  <li>Revenus 3a placé : CHF 1'000/mois</li>
                  <li>Budget mensuel retraite : CHF 5'000 puis CHF 3'177 + revenus placements</li>
                  <li><strong>Résultat :</strong> Faisable mais ajusté, recommandation de travailler à temps partiel jusqu'à 62 ans</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Lead Magnet Middle */}

          {/* Optimisation fiscale */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-8 h-8 text-yellow-400" />
              <h2 className="text-3xl font-bold text-white">Optimisation fiscale de la retraite</h2>
            </div>

            <h3 className="text-xl font-bold text-white mb-4">Impôts sur les retraits de capitaux</h3>

            <div className="space-y-4">
              <div className="p-6 bg-orange-500/10 border-l-4 border-orange-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">🎯 Stratégie 1 : Rachat LPP (si vous optez pour la RENTE)</h4>
                <p className="text-gray-300 text-sm">
                  Si vous avez des lacunes LPP et comptez prendre la RENTE (pas le capital), rachetez-les dans les 3 années précédant votre retraite pour bénéficier de la déduction fiscale immédiate. 
                  <strong>⚠️ Important :</strong> Si vous retirez le capital dans les 3 ans suivant un rachat, vous devez rembourser l'avantage fiscal.
                </p>
              </div>

              <div className="p-6 bg-orange-500/10 border-l-4 border-orange-500 rounded-r-lg">
                <h4 className="font-bold text-white mb-2">🎯 Stratégie 2 : Domicile fiscal</h4>
                <p className="text-gray-300 text-sm">
                  L'impôt sur les prestations en capital varie fortement selon les cantons. À Neuchâtel, 
                  le taux est compétitif. Certains choisissent de déménager avant le retrait (Schwyz, Nidwald).
                </p>
              </div>
            </div>
          </div>

          {/* Erreurs à éviter */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <div className="flex items-center gap-3 mb-6">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <h2 className="text-3xl font-bold text-white">7 Erreurs fréquentes à éviter</h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  title: "Oublier l'inflation",
                  desc: "Avec 2% d'inflation annuelle, CHF 5'000 aujourd'hui = CHF 3'750 de pouvoir d'achat dans 20 ans. Planifiez avec une marge de sécurité."
                },
                {
                  title: "Retirer tout le 2ème pilier en capital",
                  desc: "Un mix capital + rente peut être plus sûr. La rente garantit un revenu à vie, même si vous vivez jusqu'à 95 ans."
                },
                {
                  title: "Ne pas anticiper les imprévus",
                  desc: "Santé, aide d'un proche, travaux sur le logement : gardez une réserve d'urgence de CHF 50'000-100'000."
                },
                {
                  title: "Partir trop tôt sans simulation",
                  desc: "Faites une simulation complète avec un expert AVANT de démissionner. Un départ précipité peut être irréversible."
                },
                {
                  title: "Négliger la couverture décès/invalidité",
                  desc: "En quittant la LPP, vous perdez ces assurances. Souscrivez une assurance-vie privée si vous avez des personnes à charge."
                },
                {
                  title: "Croire que « je travaillerai un peu à côté »",
                  desc: "Trouver un emploi à temps partiel après 60 ans est difficile. Ne comptez pas dessus dans votre budget de base."
                }
              ].map((error, index) => (
                <div key={index} className="p-6 bg-red-500/10 border-l-4 border-red-500 rounded-r-lg">
                  <h4 className="font-bold text-white mb-2">❌ {error.title}</h4>
                  <p className="text-gray-300 text-sm">{error.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* WhatsApp CTA */}

          {/* FAQ */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Questions fréquentes</h2>
            
            <div className="space-y-4">
              {[
                {
                  q: "Quel est l'âge minimal pour partir en retraite ?",
                  a: "Légalement, vous pouvez arrêter de travailler à tout âge. Mais l'AVS ne peut être anticipée que de 2 ans maximum (dès 63 ans, avec réduction de 6.8%/an). Le 2ème pilier peut généralement être retiré dès 58 ans si vous cessez votre activité (selon le règlement de votre caisse de pension)."
                },
                {
                  q: "Puis-je toucher le chômage avant la retraite ?",
                  a: "Oui, jusqu'à l'âge de référence AVS (64-65 ans selon sexe et année de naissance). Mais attention : les indemnités chômage réduisent vos cotisations AVS, ce qui peut impacter votre rente future."
                },
                {
                  q: "Que se passe-t-il si je retravaille après ma retraite ?",
                  a: "Vous pouvez retravailler librement. Pour les revenus supérieurs à CHF 1'400/mois (CHF 16'800/an), vous devrez cotiser à l'AVS sans que cela augmente votre rente. Les revenus inférieurs à cette franchise sont exempts de cotisations. Le revenu sera imposé normalement."
                },
                {
                  q: "Comment est imposée la rente AVS ?",
                  a: "La rente AVS est imposée comme un revenu ordinaire (100% imposable). En revanche, les retraits de capital (LPP, 3a) sont imposés séparément à un taux réduit."
                },
                {
                  q: "Puis-je partir à 55 ans ?",
                  a: "Techniquement oui, mais c'est très difficile financièrement. Vous devrez vivre 10 ans sans AVS (qui ne peut être anticipée que de 2 ans au maximum). Cela nécessite un capital très important (>CHF 1.5M généralement) et une excellente gestion patrimoniale."
                },
                {
                  q: "La retraite anticipée est-elle fiscalement avantageuse ?",
                  a: "Cela dépend. L'impôt sur les retraits de capitaux est avantageux SI vous les échelonnez. Mais attention à ne pas vous retrouver dans une situation de revenu trop faible qui vous fait perdre des déductions."
                }
              ].map((faq, index) => (
                <div key={index} className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-purple-500/30 transition-all">
                  <h4 className="font-bold text-white mb-2 flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    {faq.q}
                  </h4>
                  <p className="text-gray-300 text-sm ml-7">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Lead Magnet Bottom */}

          {/* Conclusion */}
          <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8">
            <h2 className="text-3xl font-bold text-white mb-6">Conclusion</h2>
            <p className="text-gray-300 mb-4 leading-relaxed">
              La retraite anticipée est un objectif réalisable pour ceux qui planifient avec rigueur et anticipation. 
              Chaque situation est unique et nécessite une analyse personnalisée : capital disponible, dépenses prévues, 
              état de santé, situation familiale.
            </p>
            <p className="text-gray-300 mb-6 leading-relaxed">
              <strong className="text-white">Ne prenez pas de décision précipitée :</strong> un départ en retraite 
              trop précoce ou mal planifié peut compromettre définitivement votre sécurité financière. ATOM Group vous 
              accompagne pour construire un plan personnalisé et sécurisé.
            </p>

            <div className="p-6 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl border border-purple-500/30">
              <p className="text-white font-semibold mb-4">
                🎯 ATOM Group vous accompagne pour :
              </p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  Calculer votre faisabilité de retraite anticipée
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  Optimiser vos retraits 2ème et 3ème pilier
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  Minimiser l'impact fiscal de vos retraits
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  Construire un budget réaliste et sécurisé
                </li>
              </ul>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-700 transition-all transform hover:scale-105 text-lg"
              >
                Réserver mon analyse gratuite maintenant
                <ArrowRight className="w-6 h-6" />
              </Link>
              <p className="text-gray-400 text-sm mt-3">
                🕐 Rendez-vous de 30 minutes • 100% gratuit • Sans engagement
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Sticky Banner */}

      {/* Exit Intent Popup */}
    </div>
  );
};

export default GuidePlanificationRetraiteAnticipee;
